import { useState, useEffect, useCallback } from 'react';
import { SensorTypeService } from '../../services';
import type { SensorType } from '../../../../types';
import { formatSensorType } from '../../../../shared/utils/formatters';
import {
  getEffectiveModelFamily,
  isDisplacementCategory,
  rowBelongsToFamily,
} from '../../utils/displacementModelFamily';
import { HIDDEN_CATEGORIES } from '../../sensors/categoryUtils';
import { formatMeasurementRangeLabel } from '../../utils/rangeUnit';

export interface SensorTypeOption {
  value: string;
  label: string;
}

export interface UseSensorTypesReturn {
  availableSensorTypes: SensorTypeOption[];
  availableCategories: SensorTypeOption[];
  availableModelFamilies: SensorTypeOption[];
  availableModels: SensorTypeOption[];
  availableRanges: SensorTypeOption[];
  isLoadingSensorTypes: boolean;
  allSensorTypes: any[];
  // false when availableModelFamilies resolves to exactly one option — the
  // UI should then use that configured family directly instead of showing
  // a selection dropdown. True when there's more than one to choose from.
  familySelectionRequired: boolean;
  updateCategories: (sensorType: string) => void;
  updateModelFamilies: (sensorType: string, category: string) => void;
  updateModels: (sensorType: string, category: string, modelFamily?: string) => void;
  updateRanges: (sensorType: string, category: string, model: string) => void;
}

export const useSensorTypes = (isOpen: boolean) => {
  const [availableSensorTypes, setAvailableSensorTypes] = useState<SensorTypeOption[]>([]);
  const [allSensorTypes, setAllSensorTypes] = useState<SensorType[]>([]);
  const [availableCategories, setAvailableCategories] = useState<SensorTypeOption[]>([]);
  const [availableModelFamilies, setAvailableModelFamilies] = useState<SensorTypeOption[]>([]);
  const [familySelectionRequired, setFamilySelectionRequired] = useState(true);
  const [availableModels, setAvailableModels] = useState<SensorTypeOption[]>([]);
  const [availableRanges, setAvailableRanges] = useState<SensorTypeOption[]>([]);
  const [isLoadingSensorTypes, setIsLoadingSensorTypes] = useState(false);

  // Load sensor types once when modal opens (cache to avoid repeated API calls)
  useEffect(() => {
    const loadSensorTypes = async () => {
      // If already loaded, don't reload
      if (allSensorTypes.length > 0 && availableSensorTypes.length > 0) {
        return;
      }

      setIsLoadingSensorTypes(true);
      try {
        // Load all sensor types data (for filtering categories, models, ranges)
        const allTypes = await SensorTypeService.getAll();
        setAllSensorTypes(allTypes);

        const calibratableTypes = allTypes.filter(
          (st: SensorType) =>
            !HIDDEN_CATEGORIES.includes((st.category || "").toLowerCase())
        );

        // Extract unique sensor types for dropdown
        const allTypeValues = calibratableTypes
          .map((st: SensorType) => st.type)
          .filter((type: string | undefined): type is string => type !== undefined && type !== null && type.trim() !== '');
        
        const uniqueTypes = Array.from(new Set(allTypeValues)).sort();
        setAvailableSensorTypes(uniqueTypes.map((type: string) => ({
          value: type,
          label: formatSensorType(type)
        })));
      } catch (error) {
        setAvailableSensorTypes([]);
        setAllSensorTypes([]);
      } finally {
        setIsLoadingSensorTypes(false);
      }
    };

    if (isOpen) {
      loadSensorTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update categories when sensor type changes (using cached data, no API call)
  const updateCategories = useCallback((sensorType: string) => {
    if (sensorType && allSensorTypes.length > 0) {
      // Filter categories from cached sensor types data (exclude API-only categories)
      const categories = allSensorTypes
        .filter(st => st.type === sensorType)
        .map(st => st.category)
        .filter((category): category is string =>
          category !== undefined &&
          !HIDDEN_CATEGORIES.includes(category.toLowerCase())
        );
      
      const uniqueCategories = Array.from(new Set(categories)).sort();
      setAvailableCategories(uniqueCategories.map(category => ({
        value: category,
        label: category
      })));
    } else {
      setAvailableCategories([]);
    }
    setAvailableModelFamilies([]);
    setAvailableModels([]);
    setAvailableRanges([]);
  }, [allSensorTypes]);

  // Displacement-only: resolve sheet "Sensor type" families (EDE-VXX-*).
  // Prefers each row's configured meta_data.family; falls back to
  // deriving the family from the model name for rows not yet migrated.
  const updateModelFamilies = useCallback((sensorType: string, category: string) => {
    if (
      sensorType &&
      category &&
      isDisplacementCategory(category) &&
      allSensorTypes.length > 0
    ) {
      const rows = allSensorTypes.filter(
        st => st.type === sensorType && st.category === category
      );

      const families = rows
        .map(st => getEffectiveModelFamily(st))
        .filter((family): family is string => !!family);

      const uniqueFamilies = Array.from(new Set(families)).sort();
      setAvailableModelFamilies(
        uniqueFamilies.map(family => ({ value: family, label: family }))
      );

      // Only one possible family -> nothing to pick, resolve it automatically.
      // More than one -> the user still needs to choose.
      setFamilySelectionRequired(uniqueFamilies.length !== 1);
    } else {
      setFamilySelectionRequired(true);
      setAvailableModelFamilies([]);
    }
    setAvailableModels([]);
    setAvailableRanges([]);
  }, [allSensorTypes]);

  // Update models when category (and optional Displacement family) changes
  const updateModels = useCallback((
    sensorType: string,
    category: string,
    modelFamily?: string
  ) => {
    if (sensorType && category && allSensorTypes.length > 0) {
      let rows = allSensorTypes.filter(
        st => st.type === sensorType && st.category === category
      );

      // Displacement: require family so Model lists only that sheet group
      if (isDisplacementCategory(category)) {
        if (!modelFamily) {
          setAvailableModels([]);
          setAvailableRanges([]);
          return;
        }
        rows = rows.filter(
          st => st.model && rowBelongsToFamily(st, modelFamily)
        );
      }

      const models = rows
        .map(st => st.model)
        .filter((model): model is string => model !== undefined);

      const uniqueModels = Array.from(new Set(models)).sort();
      setAvailableModels(uniqueModels.map(model => ({
        value: model,
        label: model
      })));
    } else {
      setAvailableModels([]);
    }
    setAvailableRanges([]);
  }, [allSensorTypes]);

  // Update ranges when model changes (using cached data, no API call)
  const updateRanges = useCallback((sensorType: string, category: string, model: string) => {
    if (sensorType && category && model && allSensorTypes.length > 0) {
      // Filter ranges from cached sensor types data, sorted by numeric range
      const ranges = allSensorTypes
        .filter(st => 
          st.type === sensorType && 
          st.category === category &&
          st.model === model
        )
        .sort((a, b) => (a.measurement_range ?? 0) - (b.measurement_range ?? 0))
        .map(st => ({
          value: `${st.measurement_range}${st.unit || ''}`,
          label: `${formatMeasurementRangeLabel(st.measurement_range, st.unit, st.category, { compact: true })}${st.description ? ` - ${st.description}` : ''}`
        }));
      setAvailableRanges(ranges);
    } else {
      setAvailableRanges([]);
    }
  }, [allSensorTypes]);

  return {
    availableSensorTypes,
    availableCategories,
    availableModelFamilies,
    availableModels,
    availableRanges,
    isLoadingSensorTypes,
    allSensorTypes,
    familySelectionRequired,
    updateCategories,
    updateModelFamilies,
    updateModels,
    updateRanges,
  };
};
